import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal, Chapter } from "./Reveal";

const SPOTS = [
  {
    id: "restaurant",
    label: "Restaurant Counter",
    caption: "Right beside the till — the natural final moment of a visit.",
    bg: "/assets/scene-restaurant.webp",
  },
  {
    id: "reception",
    label: "Reception Desk",
    caption: "Where every customer pauses before they leave.",
    bg: "/assets/scene-reception.webp",
  },
  {
    id: "barber",
    label: "Barber Shop",
    caption: "On the station counter, the moment the cut is done.",
    bg: "/assets/scene-barber.webp",
  },
  {
    id: "salon",
    label: "Salon",
    caption: "At the mirror, while the result is still fresh.",
    bg: "/assets/scene-salon.webp",
  },
];

export const Placement = () => {
  const [active, setActive] = useState(SPOTS[0]);

  return (
    <section id="placement" className="bg-mist py-28 sm:py-36" data-testid="placement-section">
      <div className="max-w-7xl mx-auto px-6">
        <Chapter number="05" label="Placement" />
        <Reveal>
          <h2 className="rvr-title font-display text-4xl md:text-5xl font-bold tracking-tight text-ink leading-[1.05]">
            Where should I put it?
          </h2>
        </Reveal>
        <div className="mt-12 grid lg:grid-cols-12 gap-8">
          <Reveal className="lg:col-span-4">
            <div className="flex flex-col" data-testid="placement-options">
              {SPOTS.map((s) => (
                <button
                  key={s.id}
                  data-testid={`placement-${s.id}`}
                  onClick={() => setActive(s)}
                  className={`text-left py-5 px-5 -mx-5 rounded-sm border-b border-black/10 transition-colors duration-300 ${
                    active.id === s.id ? "bg-ink text-white" : "hover:bg-black/[0.03] text-ink"
                  }`}
                >
                  <p className="font-display text-xl font-bold">{s.label}</p>
                  {active.id === s.id && (
                    <motion.p
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      className="text-sm text-white/60 mt-1.5"
                    >
                      {s.caption}
                    </motion.p>
                  )}
                </button>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-8">
            <div className="relative aspect-[16/10] rounded-sm overflow-hidden bg-ink" data-testid="placement-visual">
              <AnimatePresence mode="wait">
                <motion.div
                  key={active.id}
                  initial={{ opacity: 0, scale: 1.04 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  className="absolute inset-0"
                >
                  <img src={active.bg} alt={`Revora NFC review stand placed ${active.label.toLowerCase()}`} className="absolute inset-0 w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-transparent" />
                  <div className="absolute top-5 left-5 bg-white/90 backdrop-blur px-4 py-2 rounded-full">
                    <p className="text-xs font-bold tracking-[0.15em] uppercase text-ink">{active.label}</p>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
